import React, { useMemo, useRef } from 'react';

import { Flex } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { updateLocalStateValues } from 'components/RabbitOKRSidePanel/KRslice';
import { useLocalState } from 'components/RabbitOKRSidePanel/PrimaryPanel/Sections/Details/useLocalState';
import { useOKRRealignMutation } from 'components/RabbitOKRSidePanel/datahooks/useOKRRealignMutation';
import { useOKRRealignViewData } from 'components/RabbitOKRSidePanel/datahooks/useOKRRealignViewData';
import {
  SECONDARY_PANEL_TYPES,
  closeRealignPanel,
  openChangeDetailsPanel,
} from 'components/RabbitOKRSidePanel/krDrawerSecondaryPanelSlice';
import { useOKRDrawerControlCallbacks } from 'components/RabbitOKRSidePanel/useOKRDrawerControlCallbacks';
import { useViewPageName } from 'components/Views/Hooks/useViewPageName';
import useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges from 'components/hooks/useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useToast } from 'components/hooks/useToast';
import { useSelector, useDispatch } from 'react-redux';
import { kRDrawerSelector } from 'selectors/kRDrawer';
import { kRDrawerSecondaryPanelSelector } from 'selectors/krSecondaryPanel';
import { okrCreationPanelStateSelector } from 'selectors/okrCreationPanelSelector';

import { OBJECTIVE_LEVEL, OKR_PROGRESS_TYPES, OKR_TYPES } from 'constants/ProjectConstants';
import { getTimePeriodIdString } from 'constants/helper';
import { sendRealignedKREvent, sendRealignedObjectiveEvent } from 'constants/mixpanelHelper';

import { RealignFooter as ParentOKRSelectionFooter } from '../RealignPanel/RealignFooter';
import { ParentOKRSelectionGrid } from './ParentOKRSelectionGrid';
import { ParentOKRSelectionHeader } from './ParentOKRSelectionHeader';

/* Currently this section is handling both OKR alignment and OKR's children realignment */
export const ParentOKRSelectionSection = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const page = useViewPageName();

  const { onUpdate } = useOKRDrawerControlCallbacks();

  const { details } = useSelector(kRDrawerSecondaryPanelSelector);
  const toast = useToast();

  const { objectivesName, keyResultsName } = useOrganisationSettings();

  const {
    okrTimePeriod,
    okrUuid,
    okrType,
    objectiveLevel,
    parentUuid,
    parentText,
    parentType,
    parentVisibilityType,
    isOpenAsChild,
  } = details;

  /* 
    read primary kr panel details, this will work but
    so far this component was relying only on secondaryPanel state
  */
  const { timeBoundDetails: okrSidePanelTimeBoundDetails } = useSelector(kRDrawerSelector);

  /* 
    read okr creation details, this will work but 
    so far this component was relying only on secondaryPanel state 
  */
  const { timeBoundDetails: okrCreationPanelTimeBoundDetails } = useSelector(okrCreationPanelStateSelector);

  const isCreateNewOKRWorkflow = okrUuid === null;

  const timeBoundDetails = isCreateNewOKRWorkflow ? okrCreationPanelTimeBoundDetails : okrSidePanelTimeBoundDetails;

  const [localSelectedTimePeriod, setLocalSelectedTimePeriod] = useLocalState(okrTimePeriod);

  const parentPost = useMemo(
    () => ({
      uuid: parentUuid
        ? String(parentUuid)
        : null /* At some place we are reading uuid as number and that causes issues in comparison */,
      text: parentText,
      type: parentType,
      visibilityType: parentVisibilityType,
    }),
    [parentUuid, parentText, parentType, parentVisibilityType]
  );

  const [selectedParent, setSelectedParent] = useLocalState(parentPost);

  const gridRef = useRef();

  const onCancel = () => {
    if (isOpenAsChild) {
      if (SECONDARY_PANEL_TYPES.CHANGE_DETAILS_PANEL === details?.parentState?.type) {
        dispatch(openChangeDetailsPanel({}));
      }
    } else {
      dispatch(closeRealignPanel());
    }
  };

  const { data, isLoading } = useOKRRealignViewData({
    timeBoundPeriod: localSelectedTimePeriod,
  });

  const { mutate, isLoading: isMutating } = useOKRRealignMutation(okrUuid);

  const onSelectParentOKR = ({ selectedParent: curSelectedParent, selectedTimePeriod }) => {
    let payload = {
      parentPost: null,
      timeBoundDetails: selectedTimePeriod,
      parentProgressType: null,
      parentMeasure: null,
      parentMeasureType: null,
      startTS: selectedTimePeriod?.startTS,
      dueTS: selectedTimePeriod?.endTS,
    };

    const isRemoveParentWorkflow = curSelectedParent === null;

    if (!isRemoveParentWorkflow) {
      const {
        uuid,
        text,
        type,
        visibilityType,
        progressType: parentProgressType,
        measure: parentMeasure,
        measureType: parentMeasureType,
      } = curSelectedParent;

      payload = {
        ...payload,
        parentPost: {
          uuid,
          text,
          type,
          visibilityType,
        },
        parentProgressType,
        parentMeasureType,
        parentMeasure,
      };

      /* for derived sum parent progress type set progress Type as not independent (if not already ) */
      if (parentProgressType === OKR_PROGRESS_TYPES.DERIVED_SUM) {
        payload.progressType = OKR_PROGRESS_TYPES.MANUAL;
        payload.measureType = parentMeasureType;
        payload.measure = parentMeasure;
      }
    }

    if (okrType === OKR_TYPES.OBJECTIVE) {
      sendRealignedObjectiveEvent({
        eventProperties: {
          goalId: okrUuid,
          type: details?.eventType || 'Realign',
          source: details?.source || 'Kebab Menu',
          page,
        },
      });
    } else {
      sendRealignedKREvent({
        eventProperties: {
          goalId: okrUuid,
          type: details?.eventType || 'Realign',
          source: details?.source || 'Kebab Menu',
          page,
        },
      });
    }
    if (details.isOpenAsChild) {
      dispatch(updateLocalStateValues({ ...payload }));

      dispatch(openChangeDetailsPanel());
    }
  };

  const onRealign = () => {
    onSelectParentOKR({
      selectedParent,
      selectedTimePeriod: localSelectedTimePeriod,
    });
  };

  const onRemoveAlignment = () => {
    onSelectParentOKR({
      selectedParent: null,
      selectedTimePeriod: localSelectedTimePeriod,
    });
  };

  /* 
    Following code is to handle remove alignment based on privileges
    Starts here
  */
  const { canCreateKeyResultAtRootLevel } = useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges();

  const cannotRealignKeyResultAtRoot = okrType !== OKR_TYPES.OBJECTIVE && !canCreateKeyResultAtRootLevel;

  const cannotRealignObjectiveAtRoot = okrType === OKR_TYPES.OBJECTIVE && objectiveLevel !== OBJECTIVE_LEVEL.COMPANY;

  /* 
    Above code is to handle access based on config and privileges.
    Ends here
  */

  const isSelectedTimelineSameAsCurrentOKRTimeline =
    getTimePeriodIdString(timeBoundDetails) === getTimePeriodIdString(localSelectedTimePeriod);

  const hideRealignAsRoot =
    !parentUuid ||
    cannotRealignKeyResultAtRoot ||
    cannotRealignObjectiveAtRoot ||
    !isSelectedTimelineSameAsCurrentOKRTimeline;

  const canRealignThisOKRAtRoot =
    okrType === OKR_TYPES.OBJECTIVE ? objectiveLevel === OBJECTIVE_LEVEL.COMPANY : canCreateKeyResultAtRootLevel;

  const showRealignInThisTimelineAsRoot = !isSelectedTimelineSameAsCurrentOKRTimeline && canRealignThisOKRAtRoot;

  const handleRealignInThisTimelineAsRoot = onRemoveAlignment;

  return (
    <Flex flexDirection="column" flex={1}>
      <ParentOKRSelectionHeader
        gridRef={gridRef}
        localSelectedTimePeriod={localSelectedTimePeriod}
        setLocalSelectedTimePeriod={setLocalSelectedTimePeriod}
      />

      <ParentOKRSelectionGrid
        selectedParent={selectedParent}
        setSelectedParent={setSelectedParent}
        gridRef={gridRef}
        gridData={data}
      />
      <ParentOKRSelectionFooter
        showRealignInThisTimelineAsRoot={showRealignInThisTimelineAsRoot}
        handleRealignInThisTimelineAsRoot={handleRealignInThisTimelineAsRoot}
        hideRealignAsRoot={hideRealignAsRoot}
        onCancel={onCancel}
        isMutating={isMutating}
        isRemovingAlignment={false}
        disableRealign={isLoading || selectedParent?.uuid === parentUuid}
        onRemoveAlignment={onRemoveAlignment}
        realignButtonText="Realign"
        onRealign={onRealign}
      />
    </Flex>
  );
};
